import { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';

type Category = {
    _id: string;
    id: string;
    name: string;
    slug: string;
};

type GlobalDiscount = {
    _id: string;
    id: string;
    name: string;
    description?: string;
    discountType: 'percentage' | 'fixed';
    discountValue: number;
    tarifas: number[];
    categories: Category[];
    isActive: boolean;
    startDate?: string;
    endDate?: string;
    priority: number;
    firstPurchaseOnly: boolean;
    createdAt: string;
    updatedAt: string;
};

const GlobalDiscountsPage = () => {
    const [discounts, setDiscounts] = useState<GlobalDiscount[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingDiscount, setEditingDiscount] = useState<GlobalDiscount | null>(null);
    const [categories, setCategories] = useState<Category[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        discountType: 'percentage' as 'percentage' | 'fixed',
        discountValue: 0,
        tarifas: [] as number[],
        categories: [] as string[],
        isActive: true,
        startDate: '',
        endDate: '',
        priority: 0,
        firstPurchaseOnly: false
    });

    const tarifaOptions = [
        { value: 1, label: 'Tarifa 1 (Retail)' },
        { value: 7, label: 'Tarifa 7 (Distribuidor pequeño)' },
        { value: 12, label: 'Tarifa 12 (Distribuidor mediano)' },
        { value: 25, label: 'Tarifa 25 (Mayorista)' }
    ];
    tarifaOptions.splice(2, 0, { value: 10, label: 'Tarifa 10' });

    useEffect(() => {
        fetchDiscounts();
        fetchCategories();
    }, []);

    const getAuthHeaders = () => {
        const token = localStorage.getItem('auth_token');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    };

    const fetchDiscounts = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/discounts`, {
                headers: getAuthHeaders()
            });
            const data = await res.json();
            if (data.success) {
                setDiscounts(data.data);
            }
        } catch (err) {
            console.error('Error fetching discounts:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await fetch(`${API_URL}/categories`);
            const data = await res.json();
            if (data.success) {
                setCategories(data.data);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            discountType: 'percentage',
            discountValue: 0,
            tarifas: [],
            categories: [],
            isActive: true,
            startDate: '',
            endDate: '',
            priority: 0,
            firstPurchaseOnly: false
        });
        setEditingDiscount(null);
        setError(null);
    };

    const handleEdit = (discount: GlobalDiscount) => {
        setEditingDiscount(discount);
        setFormData({
            name: discount.name,
            description: discount.description || '',
            discountType: discount.discountType,
            discountValue: discount.discountValue,
            tarifas: discount.tarifas || [],
            categories: discount.categories?.map(c => c._id || c.id) || [],
            isActive: discount.isActive,
            startDate: discount.startDate ? discount.startDate.split('T')[0] : '',
            endDate: discount.endDate ? discount.endDate.split('T')[0] : '',
            priority: discount.priority || 0,
            firstPurchaseOnly: discount.firstPurchaseOnly || false
        });
        setShowForm(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        try {
            const payload = {
                ...formData,
                startDate: formData.startDate || null,
                endDate: formData.endDate || null
            };

            const url = editingDiscount
                ? `${API_URL}/discounts/${editingDiscount._id}`
                : `${API_URL}/discounts`;

            const res = await fetch(url, {
                method: editingDiscount ? 'PUT' : 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (data.success) {
                await fetchDiscounts();
                setShowForm(false);
                resetForm();
            } else {
                setError(data.error || 'Error al guardar el descuento');
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (discount: GlobalDiscount) => {
        try {
            const res = await fetch(`${API_URL}/discounts/${discount._id}/toggle`, {
                method: 'PATCH',
                headers: getAuthHeaders()
            });

            if (res.ok) {
                await fetchDiscounts();
            }
        } catch (err) {
            console.error('Error toggling discount:', err);
        }
    };

    const handleDelete = async (discount: GlobalDiscount) => {
        if (!confirm(`¿Estás seguro de eliminar el descuento "${discount.name}"?`)) {
            return;
        }

        try {
            const res = await fetch(`${API_URL}/discounts/${discount._id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (res.ok) {
                await fetchDiscounts();
            }
        } catch (err) {
            console.error('Error deleting discount:', err);
        }
    };

    const handleTarifaToggle = (tarifa: number) => {
        setFormData(prev => ({
            ...prev,
            tarifas: prev.tarifas.includes(tarifa)
                ? prev.tarifas.filter(t => t !== tarifa)
                : [...prev.tarifas, tarifa]
        }));
    };

    const handleCategoryToggle = (categoryId: string) => {
        setFormData(prev => ({
            ...prev,
            categories: prev.categories.includes(categoryId)
                ? prev.categories.filter(c => c !== categoryId)
                : [...prev.categories, categoryId]
        }));
    };

    const formatDate = (date: string | undefined) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('es-ES');
    };

    return (
        <AdminLayout title="Descuentos Globales">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <p className="text-gray-600">
                        Gestiona los descuentos globales que se aplican automáticamente a productos según tarifa y categoría.
                    </p>
                    <button
                        onClick={() => { resetForm(); setShowForm(true); }}
                        className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                    >
                        + Nuevo descuento
                    </button>
                </div>

                {/* Form Modal */}
                {showForm && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold">
                                        {editingDiscount ? 'Editar descuento' : 'Nuevo descuento'}
                                    </h2>
                                    <button
                                        onClick={() => { setShowForm(false); resetForm(); }}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                {error && (
                                    <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
                                        {error}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {/* Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Nombre *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                            placeholder="Ej: Rebajas de Enero"
                                        />
                                    </div>

                                    {/* Description */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Descripción
                                        </label>
                                        <textarea
                                            value={formData.description}
                                            onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                            rows={2}
                                            placeholder="Descripción opcional del descuento"
                                        />
                                    </div>

                                    {/* Discount Type & Value */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Tipo de descuento *
                                            </label>
                                            <select
                                                value={formData.discountType}
                                                onChange={e => setFormData(prev => ({ ...prev, discountType: e.target.value as 'percentage' | 'fixed' }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                            >
                                                <option value="percentage">Porcentaje (%)</option>
                                                <option value="fixed">Valor fijo (€)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Valor *
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    required
                                                    min="0"
                                                    max={formData.discountType === 'percentage' ? 100 : undefined}
                                                    step="0.01"
                                                    value={formData.discountValue}
                                                    onChange={e => setFormData(prev => ({ ...prev, discountValue: parseFloat(e.target.value) || 0 }))}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                                />
                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                                                    {formData.discountType === 'percentage' ? '%' : '€'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tarifas */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Aplicar a tarifas (vacío = todas)
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {tarifaOptions.map(opt => (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() => handleTarifaToggle(opt.value)}
                                                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${formData.tarifas.includes(opt.value)
                                                        ? 'bg-black text-white border-black'
                                                        : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                                                        }`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                        {formData.tarifas.length === 0 && (
                                            <p className="text-xs text-gray-500 mt-1">Se aplicará a todas las tarifas</p>
                                        )}
                                    </div>

                                    {/* Categories */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Aplicar a categorías (vacío = todas)
                                        </label>
                                        <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2">
                                            {categories.length === 0 ? (
                                                <p className="text-gray-500 text-sm">Cargando categorías...</p>
                                            ) : (
                                                <div className="space-y-1">
                                                    {categories.map(cat => (
                                                        <label key={cat._id || cat.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                                                            <input
                                                                type="checkbox"
                                                                checked={formData.categories.includes(cat._id || cat.id)}
                                                                onChange={() => handleCategoryToggle(cat._id || cat.id)}
                                                                className="rounded border-gray-300"
                                                            />
                                                            <span className="text-sm">{cat.name}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        {formData.categories.length === 0 && (
                                            <p className="text-xs text-gray-500 mt-1">Se aplicará a todas las categorías</p>
                                        )}
                                    </div>

                                    {/* Dates */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Fecha inicio (opcional)
                                            </label>
                                            <input
                                                type="date"
                                                value={formData.startDate}
                                                onChange={e => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Fecha fin (opcional)
                                            </label>
                                            <input
                                                type="date"
                                                value={formData.endDate}
                                                onChange={e => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                            />
                                        </div>
                                    </div>

                                    {/* Priority & Active */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Prioridad
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.priority}
                                                onChange={e => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                                placeholder="0"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Mayor prioridad = se aplica primero</p>
                                        </div>
                                        <div className="flex items-center pt-6">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.isActive}
                                                    onChange={e => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                                                    className="w-5 h-5 rounded border-gray-300"
                                                />
                                                <span className="text-sm font-medium">Descuento activo</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* First Purchase Only */}
                                    <div className="p-4 bg-blue-50 rounded-lg">
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formData.firstPurchaseOnly}
                                                onChange={e => setFormData(prev => ({ ...prev, firstPurchaseOnly: e.target.checked }))}
                                                className="w-5 h-5 rounded border-gray-300"
                                            />
                                            <div>
                                                <span className="text-sm font-medium text-gray-900">Solo primera compra</span>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    Este descuento solo se aplicará a usuarios que no hayan realizado ninguna compra anteriormente
                                                </p>
                                            </div>
                                        </label>
                                    </div>

                                    {/* Submit */}
                                    <div className="flex justify-end gap-3 pt-4 border-t">
                                        <button
                                            type="button"
                                            onClick={() => { setShowForm(false); resetForm(); }}
                                            className="px-4 py-2 text-gray-700 hover:text-gray-900"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={saving}
                                            className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
                                        >
                                            {saving ? 'Guardando...' : (editingDiscount ? 'Guardar cambios' : 'Crear descuento')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Discounts List */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
                        <p className="mt-4 text-gray-600">Cargando descuentos...</p>
                    </div>
                ) : discounts.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-semibold text-gray-900">No hay descuentos</h3>
                        <p className="mt-1 text-sm text-gray-500">Crea tu primer descuento global para la tienda.</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Nombre
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Descuento
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tarifas
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Categorías
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Vigencia
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Estado
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {discounts.map(discount => (
                                    <tr key={discount._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="font-medium text-gray-900">{discount.name}</div>
                                            {discount.description && (
                                                <div className="text-sm text-gray-500 truncate max-w-xs">{discount.description}</div>
                                            )}
                                            {discount.firstPurchaseOnly && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                                                    Solo 1ª compra
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                {discount.discountType === 'percentage'
                                                    ? `-${discount.discountValue}%`
                                                    : `-${discount.discountValue}€`}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {discount.tarifas && discount.tarifas.length > 0
                                                ? discount.tarifas.join(', ')
                                                : 'Todas'}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            <div className="max-w-xs truncate">
                                                {discount.categories && discount.categories.length > 0
                                                    ? discount.categories.map(c => c.name).join(', ')
                                                    : 'Todas'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {discount.startDate || discount.endDate ? (
                                                <span>
                                                    {formatDate(discount.startDate)} - {formatDate(discount.endDate)}
                                                </span>
                                            ) : (
                                                'Sin límite'
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <button
                                                onClick={() => handleToggle(discount)}
                                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${discount.isActive
                                                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {discount.isActive ? 'Activo' : 'Inactivo'}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => handleEdit(discount)}
                                                className="text-indigo-600 hover:text-indigo-900 mr-4"
                                            >
                                                Editar
                                            </button>
                                            <button
                                                onClick={() => handleDelete(discount)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                Eliminar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default GlobalDiscountsPage;
